import React, { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import {
    Bell, Mail, MessageSquare, Monitor, Plus, RefreshCw,
    Search, Send, Clock, FileText, XCircle, CheckCircle,
    Eye, Users, BarChart2, Settings, ChevronRight, Zap,
    Target, Layers, Calendar, Filter, TrendingUp, AlertCircle,
    ArrowUpRight, MoreVertical, Sparkles, Activity
} from 'lucide-react'
import { FaWhatsapp } from 'react-icons/fa'
import styles from './NotificationHome.module.css'

// ─── CONSTANTS ────────────────────────────────────────────────────────────────
const CHANNELS = [
    { key: 'ALL',    label: 'All Channels',     Icon: Layers,        color: '#6366f1', bg: '#eef2ff', border: '#c7d2fe' },
    { key: 'PUSH',   label: 'Push Notification', Icon: Bell,          color: '#0369a1', bg: '#f0f9ff', border: '#7dd3fc' },
    { key: 'SMS',    label: 'SMS',               Icon: MessageSquare, color: '#15803d', bg: '#f0fdf4', border: '#86efac' },
    { key: 'EMAIL',  label: 'Email',             Icon: Mail,          color: '#b45309', bg: '#fffbeb', border: '#fcd34d' },
    { key: 'WA',     label: 'WhatsApp',          Icon: null,          color: '#15803d', bg: '#dcfce7', border: '#4ade80', isWa: true },
    { key: 'IN_APP', label: 'In-App',            Icon: Monitor,       color: '#7c3aed', bg: '#faf5ff', border: '#ddd6fe' },
]

const STATUSES = [
    { key: 'ALL',       label: 'All',       Icon: Layers   },
    { key: 'SENT',      label: 'Sent',      Icon: Send     },
    { key: 'SCHEDULED', label: 'Scheduled', Icon: Clock    },
    { key: 'DRAFT',     label: 'Draft',     Icon: FileText },
    { key: 'FAILED',    label: 'Failed',    Icon: XCircle  },
]

const STATUS_CFG = {
    SENT:      { color: '#15803d', bg: '#f0fdf4', border: '#86efac', label: 'Sent'      },
    SCHEDULED: { color: '#d97706', bg: '#fffbeb', border: '#fcd34d', label: 'Scheduled' },
    DRAFT:     { color: '#6b7280', bg: '#f9fafb', border: '#e5e7eb', label: 'Draft'     },
    FAILED:    { color: '#dc2626', bg: '#fef2f2', border: '#fca5a5', label: 'Failed'    },
}

const CHANNEL_CFG = {
    PUSH:   { color: '#0369a1', bg: '#f0f9ff', border: '#7dd3fc', label: 'Push'     },
    SMS:    { color: '#15803d', bg: '#f0fdf4', border: '#86efac', label: 'SMS'      },
    EMAIL:  { color: '#b45309', bg: '#fffbeb', border: '#fcd34d', label: 'Email'    },
    WA:     { color: '#15803d', bg: '#dcfce7', border: '#4ade80', label: 'WhatsApp' },
    IN_APP: { color: '#7c3aed', bg: '#faf5ff', border: '#ddd6fe', label: 'In-App'  },
}

// ─── MOCK DATA ─────────────────────────────────────────────────────────────────
const NOTIFICATIONS = [
    {
        id: 'NTF-001',
        title: 'Your profile is 80% complete!',
        message: 'Complete your profile to get discovered by top salons. Add your portfolio photos and experience.',
        channel: 'PUSH', status: 'SENT',
        audience: 'Incomplete Profiles', audienceCount: 12450,
        sentAt: '2026-06-04T10:30:00', scheduledAt: null,
        stats: { sent: 12450, delivered: 11800, opened: 4320, clicked: 890, failed: 650 },
        tag: 'Engagement',
    },
    {
        id: 'NTF-002',
        title: 'New jobs in your city!',
        message: '5 new salon jobs just posted in Delhi NCR. Apply now before positions fill up.',
        channel: 'WA', status: 'SENT',
        audience: 'Active Job Seekers', audienceCount: 8920,
        sentAt: '2026-06-04T08:00:00', scheduledAt: null,
        stats: { sent: 8920, delivered: 8750, opened: 5610, clicked: 2340, failed: 170 },
        tag: 'Jobs',
    },
    {
        id: 'NTF-003',
        title: 'Exclusive June Offer — Premium Subscription',
        message: 'Get 3 months of Stylelink Premium at 50% off. Offer valid till June 30th.',
        channel: 'EMAIL', status: 'SCHEDULED',
        audience: 'Free Plan Users', audienceCount: 24600,
        sentAt: null, scheduledAt: '2026-06-06T09:00:00',
        stats: { sent: 0, delivered: 0, opened: 0, clicked: 0, failed: 0 },
        tag: 'Promotion',
    },
    {
        id: 'NTF-004',
        title: 'Weekly activity digest',
        message: "Here's a summary of your profile views, applications, and messages from this week.",
        channel: 'IN_APP', status: 'SENT',
        audience: 'All Active Users', audienceCount: 45200,
        sentAt: '2026-06-03T18:00:00', scheduledAt: null,
        stats: { sent: 45200, delivered: 44900, opened: 19800, clicked: 3100, failed: 300 },
        tag: 'Weekly Digest',
    },
    {
        id: 'NTF-005',
        title: 'Interview confirmed for tomorrow',
        message: 'Your interview at Lakme Salon, Connaught Place is confirmed for 5th June at 11 AM.',
        channel: 'SMS', status: 'SENT',
        audience: 'Shortlisted Candidates', audienceCount: 342,
        sentAt: '2026-06-04T16:00:00', scheduledAt: null,
        stats: { sent: 342, delivered: 340, opened: 340, clicked: 0, failed: 2 },
        tag: 'Transactional',
    },
    {
        id: 'NTF-006',
        title: "Re-engagement: We miss you!",
        message: "You haven't logged in for 30 days. Check out what's new on Stylelink.",
        channel: 'PUSH', status: 'DRAFT',
        audience: 'Inactive Users (30d)', audienceCount: 18700,
        sentAt: null, scheduledAt: null,
        stats: { sent: 0, delivered: 0, opened: 0, clicked: 0, failed: 0 },
        tag: 'Re-engagement',
    },
    {
        id: 'NTF-007',
        title: 'Referral bonus credited!',
        message: 'Great news! ₹200 has been credited to your wallet for referring a friend.',
        channel: 'WA', status: 'SENT',
        audience: 'Successful Referrers', audienceCount: 1240,
        sentAt: '2026-06-04T14:30:00', scheduledAt: null,
        stats: { sent: 1240, delivered: 1238, opened: 1180, clicked: 890, failed: 2 },
        tag: 'Transactional',
    },
    {
        id: 'NTF-008',
        title: 'Flash hiring drive — Enroll now',
        message: 'Join our 2-day placement drive starting June 10. Limited seats available!',
        channel: 'SMS', status: 'FAILED',
        audience: 'Freshers & Entry Level', audienceCount: 5600,
        sentAt: '2026-06-03T11:00:00', scheduledAt: null,
        stats: { sent: 1200, delivered: 800, opened: 0, clicked: 0, failed: 4400 },
        tag: 'Campaigns',
    },
]

const SUMMARY_STATS = [
    { label: 'Total Sent', value: '74,532', delta: '+12.4%', up: true,  Icon: Send,        color: '#6366f1' },
    { label: 'Delivered',  value: '72,828', delta: '+11.8%', up: true,  Icon: CheckCircle, color: '#15803d' },
    { label: 'Opened',     value: '31,250', delta: '+8.2%',  up: true,  Icon: Eye,         color: '#0369a1' },
    { label: 'CTR',        value: '9.84%',  delta: '+1.2%',  up: true,  Icon: TrendingUp,  color: '#d97706' },
    { label: 'Failed',     value: '5,322',  delta: '-3.1%',  up: false, Icon: XCircle,     color: '#dc2626' },
    { label: 'Scheduled',  value: '3',      delta: '',       up: null,  Icon: Clock,       color: '#7c3aed' },
]

const CHANNEL_STATS = {
    ALL:    { sent: 74532 },
    PUSH:   { sent: 12450 },
    SMS:    { sent: 5942  },
    EMAIL:  { sent: 24600 },
    WA:     { sent: 10160 },
    IN_APP: { sent: 45200 },
}

// ─── UTILS ────────────────────────────────────────────────────────────────────
function timeAgo(dateStr) {
    if (!dateStr) return '—'
    const diffMin = Math.floor((Date.now() - new Date(dateStr).getTime()) / 60000)
    if (diffMin < 1) return 'Just now'
    if (diffMin < 60) return `${diffMin}m ago`
    const diffHr = Math.floor(diffMin / 60)
    if (diffHr < 24) return `${diffHr}h ago`
    return `${Math.floor(diffHr / 24)}d ago`
}

function formatDate(dateStr) {
    if (!dateStr) return '—'
    return new Date(dateStr).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit', hour12: true })
}

function pct(num, den) {
    if (!den) return '0%'
    return (num / den * 100).toFixed(1) + '%'
}

function fmtNum(n) {
    if (n >= 1000000) return (n / 1000000).toFixed(1) + 'M'
    if (n >= 1000) return (n / 1000).toFixed(1) + 'K'
    return String(n)
}

// ─── BADGES ───────────────────────────────────────────────────────────────────
function ChannelBadge({ channel }) {
    const cfg = CHANNEL_CFG[channel] || { color: '#6b7280', bg: '#f9fafb', border: '#e5e7eb', label: channel }
    const ch = CHANNELS.find(c => c.key === channel)
    return (
        <span className={styles.badge} style={{ color: cfg.color, background: cfg.bg, borderColor: cfg.border }}>
            {ch?.isWa ? <FaWhatsapp size={10} /> : ch?.Icon ? <ch.Icon size={10} /> : null}
            {cfg.label}
        </span>
    )
}

function StatusBadge({ status }) {
    const cfg = STATUS_CFG[status] || { color: '#6b7280', bg: '#f9fafb', border: '#e5e7eb', label: status }
    return (
        <span className={styles.badge} style={{ color: cfg.color, background: cfg.bg, borderColor: cfg.border }}>
            {cfg.label}
        </span>
    )
}

// ─── NOTIFICATION CARD ────────────────────────────────────────────────────────
function NotifCard({ item, isSelected, onSelect }) {
    return (
        <div
            className={`${styles.notifCard} ${isSelected ? styles.notifCardSelected : ''}`}
            onClick={() => onSelect(item)}
        >
            <div className={styles.ncTop}>
                <div className={styles.ncTitles}>
                    <div className={styles.ncTitle}>{item.title}</div>
                    <div className={styles.ncMsg}>{item.message}</div>
                </div>
                <button className={styles.moreBtn} onClick={e => e.stopPropagation()}>
                    <MoreVertical size={14} />
                </button>
            </div>

            <div className={styles.ncMeta}>
                <ChannelBadge channel={item.channel} />
                <StatusBadge status={item.status} />
                <span className={styles.tagChip}>{item.tag}</span>
            </div>

            <div className={styles.ncAudience}>
                <Users size={11} color="#9ca3af" />
                <span>{item.audience}</span>
                <span className={styles.dot}>·</span>
                <span>{fmtNum(item.audienceCount)} recipients</span>
            </div>

            {item.status === 'SENT' && (
                <div className={styles.ncStats}>
                    <div className={styles.ncStat}>
                        <CheckCircle size={11} color="#15803d" />
                        <span style={{ color: '#15803d', fontWeight: 700 }}>{pct(item.stats.delivered, item.stats.sent)}</span>
                        <span className={styles.ncStatLabel}>Delivered</span>
                    </div>
                    <div className={styles.ncStat}>
                        <Eye size={11} color="#0369a1" />
                        <span style={{ color: '#0369a1', fontWeight: 700 }}>{pct(item.stats.opened, item.stats.delivered)}</span>
                        <span className={styles.ncStatLabel}>Opened</span>
                    </div>
                    <div className={styles.ncStat}>
                        <XCircle size={11} color="#dc2626" />
                        <span style={{ color: '#dc2626', fontWeight: 700 }}>{fmtNum(item.stats.failed)}</span>
                        <span className={styles.ncStatLabel}>Failed</span>
                    </div>
                </div>
            )}

            <div className={styles.ncTime}>
                {item.status === 'SCHEDULED'
                    ? <><Clock size={11} /><span>Scheduled: {formatDate(item.scheduledAt)}</span></>
                    : item.sentAt
                        ? <><Send size={11} /><span>{timeAgo(item.sentAt)}</span></>
                        : <><FileText size={11} /><span>Draft — not scheduled</span></>
                }
            </div>
        </div>
    )
}

// ─── DETAIL PANEL ─────────────────────────────────────────────────────────────
function DetailPanel({ item }) {
    if (!item) {
        return (
            <div className={styles.emptyDetail}>
                <Bell size={36} color="#e5e7eb" />
                <div className={styles.emptyDetailTitle}>No notification selected</div>
                <div className={styles.emptyDetailSub}>Select any notification to view its details and performance metrics.</div>
            </div>
        )
    }

    const { stats } = item
    const deliveryRate = pct(stats.delivered, stats.sent)
    const openRate     = pct(stats.opened, stats.delivered)
    const ctr          = pct(stats.clicked, stats.opened)

    return (
        <div className={styles.detailScroll}>
            <div className={styles.detailHeader}>
                <span className={styles.detailId}>{item.id}</span>
                <div style={{ display: 'flex', gap: 6 }}>
                    <ChannelBadge channel={item.channel} />
                    <StatusBadge status={item.status} />
                </div>
            </div>

            <div className={styles.detailTitle}>{item.title}</div>
            <div className={styles.detailMsg}>{item.message}</div>

            <div className={styles.detailSection}>
                <div className={styles.detailSecTitle}><Target size={12} /> Audience</div>
                <div className={styles.detailAudRow}>
                    <span className={styles.detailAudName}>{item.audience}</span>
                    <span className={styles.detailAudCount}>{item.audienceCount.toLocaleString()} recipients</span>
                </div>
            </div>

            <div className={styles.detailSection}>
                <div className={styles.detailSecTitle}><Calendar size={12} /> Timing</div>
                {item.sentAt && (
                    <div className={styles.timingRow}>
                        <span className={styles.timingLabel}>Sent at</span>
                        <span className={styles.timingVal}>{formatDate(item.sentAt)}</span>
                    </div>
                )}
                {item.scheduledAt && (
                    <div className={styles.timingRow}>
                        <span className={styles.timingLabel}>Scheduled</span>
                        <span className={styles.timingVal}>{formatDate(item.scheduledAt)}</span>
                    </div>
                )}
                {!item.sentAt && !item.scheduledAt && (
                    <div className={styles.timingLabel}>Not scheduled yet</div>
                )}
            </div>

            {item.status === 'SENT' && (
                <div className={styles.detailSection}>
                    <div className={styles.detailSecTitle}><BarChart2 size={12} /> Performance</div>
                    <div className={styles.perfGrid}>
                        {[
                            { label: 'Sent',      val: fmtNum(stats.sent),      sub: '',            color: '#6366f1' },
                            { label: 'Delivered', val: fmtNum(stats.delivered), sub: deliveryRate,  color: '#15803d' },
                            { label: 'Opened',    val: fmtNum(stats.opened),    sub: openRate,      color: '#0369a1' },
                            { label: 'Clicked',   val: fmtNum(stats.clicked),   sub: ctr + ' CTR',  color: '#d97706' },
                        ].map(p => (
                            <div key={p.label} className={styles.perfCard}>
                                <div className={styles.perfVal} style={{ color: p.color }}>{p.val}</div>
                                <div className={styles.perfLabel}>{p.label}</div>
                                {p.sub && <div className={styles.perfRate}>{p.sub}</div>}
                            </div>
                        ))}
                    </div>

                    <div className={styles.funnelWrap}>
                        <div className={styles.funnelTrack}>
                            <div className={styles.funnelBar} style={{ width: '100%',      background: '#e0e7ff' }} />
                            <div className={styles.funnelBar} style={{ width: deliveryRate, background: '#a5b4fc' }} />
                            <div className={styles.funnelBar} style={{ width: openRate,     background: '#6366f1' }} />
                        </div>
                        <div className={styles.funnelLegend}>
                            <span><span style={{ color: '#e0e7ff' }}>■</span> Sent</span>
                            <span><span style={{ color: '#a5b4fc' }}>■</span> Delivered</span>
                            <span><span style={{ color: '#6366f1' }}>■</span> Opened</span>
                        </div>
                    </div>

                    {stats.failed > 0 && (
                        <div className={styles.failedAlert}>
                            <AlertCircle size={12} />
                            <span>{fmtNum(stats.failed)} messages failed to deliver</span>
                        </div>
                    )}
                </div>
            )}

            <div className={styles.detailActions}>
                {item.status === 'DRAFT' && (
                    <>
                        <button className={styles.actPrimary}><Zap size={13} /> Send Now</button>
                        <button className={styles.actSecondary}><Calendar size={13} /> Schedule</button>
                    </>
                )}
                {item.status === 'SCHEDULED' && (
                    <>
                        <button className={styles.actDanger}><XCircle size={13} /> Cancel</button>
                        <button className={styles.actSecondary}><Settings size={13} /> Edit</button>
                    </>
                )}
                {(item.status === 'SENT' || item.status === 'FAILED') && (
                    <button className={styles.actSecondary}><Layers size={13} /> Duplicate</button>
                )}
                <button className={styles.actGhost}><BarChart2 size={13} /> Full Report</button>
            </div>
        </div>
    )
}

// ─── MAIN COMPONENT ───────────────────────────────────────────────────────────
const NotificationHome = () => {
    const [activeChannel, setActiveChannel] = useState('ALL')
    const [activeStatus,  setActiveStatus]  = useState('ALL')
    const [searchQuery,   setSearchQuery]   = useState('')
    const [selectedNotif, setSelectedNotif] = useState(null)
    const [spinning,      setSpinning]      = useState(false)

    const filtered = useMemo(() => NOTIFICATIONS.filter(n => {
        if (activeChannel !== 'ALL' && n.channel !== activeChannel) return false
        if (activeStatus  !== 'ALL' && n.status  !== activeStatus)  return false
        if (searchQuery.trim()) {
            const q = searchQuery.toLowerCase()
            return n.title.toLowerCase().includes(q) || n.message.toLowerCase().includes(q) || n.audience.toLowerCase().includes(q)
        }
        return true
    }), [activeChannel, activeStatus, searchQuery])

    const statusCounts = useMemo(() => {
        const base = activeChannel === 'ALL' ? NOTIFICATIONS : NOTIFICATIONS.filter(n => n.channel === activeChannel)
        return STATUSES.reduce((acc, s) => {
            acc[s.key] = s.key === 'ALL' ? base.length : base.filter(n => n.status === s.key).length
            return acc
        }, {})
    }, [activeChannel])

    const handleRefresh = () => {
        setSpinning(true)
        setTimeout(() => setSpinning(false), 1200)
    }

    return (
        <div className={styles.root}>

            {/* ── HEADER ───────────────────────────────────────────────── */}
            <div className={styles.header}>
                <div className={styles.headerLeft}>
                    <div className={styles.headerIconBox}><Zap size={14} color="#6366f1" /></div>
                    <span className={styles.pageTitle}>Notification Engine</span>
                    <span className={styles.totalBadge}>{NOTIFICATIONS.length} notifications</span>
                </div>
                <div className={styles.headerRight}>
                    <button className={styles.iconBtn} onClick={handleRefresh} title="Refresh">
                        <RefreshCw size={13} className={spinning ? styles.spin : ''} />
                    </button>
                    <button className={styles.iconBtn} title="Settings"><Settings size={13} /></button>
                    <Link to="/notifications/create" className={styles.createBtn}><Plus size={13} /> Create Notification</Link>
                </div>
            </div>

            {/* ── SUMMARY STRIP ────────────────────────────────────────── */}
            <div className={styles.summaryStrip}>
                {SUMMARY_STATS.map(s => (
                    <div key={s.label} className={styles.summaryCard}>
                        <div className={styles.summaryIcon} style={{ background: s.color + '18' }}>
                            <s.Icon size={13} color={s.color} />
                        </div>
                        <div className={styles.summaryBody}>
                            <div className={styles.summaryVal}>{s.value}</div>
                            <div className={styles.summaryLabel}>{s.label}</div>
                        </div>
                        {s.delta && (
                            <div className={styles.summaryDelta} style={{ color: s.up ? '#15803d' : '#dc2626' }}>
                                <ArrowUpRight size={10} style={{ transform: s.up ? 'none' : 'rotate(90deg)' }} />
                                {s.delta}
                            </div>
                        )}
                    </div>
                ))}
            </div>

            {/* ── BODY ─────────────────────────────────────────────────── */}
            <div className={styles.body}>

                {/* LEFT SIDEBAR */}
                <div className={styles.sidebar}>
                    <div className={styles.sideLabel}>Channels</div>
                    {CHANNELS.map(ch => {
                        const isActive = activeChannel === ch.key
                        return (
                            <button
                                key={ch.key}
                                className={`${styles.channelBtn} ${isActive ? styles.channelBtnActive : ''}`}
                                style={isActive ? { borderColor: ch.border, background: ch.bg, color: ch.color } : {}}
                                onClick={() => { setActiveChannel(ch.key); setSelectedNotif(null) }}
                            >
                                <span className={styles.chIcon} style={isActive ? { color: ch.color } : {}}>
                                    {ch.isWa ? <FaWhatsapp size={14} /> : ch.Icon ? <ch.Icon size={14} /> : null}
                                </span>
                                <span className={styles.chBody}>
                                    <span className={styles.chLabel}>{ch.label}</span>
                                    <span className={styles.chStat}>{fmtNum(CHANNEL_STATS[ch.key].sent)} sent</span>
                                </span>
                                {isActive && <ChevronRight size={11} />}
                            </button>
                        )
                    })}

                    <div className={styles.sideDivider} />
                    <div className={styles.sideLabel}>Quick Access</div>
                    {[
                        { Icon: Layers,   label: 'Templates'  },
                        { Icon: Target,   label: 'Audiences'  },
                        { Icon: Activity, label: 'Logs'       },
                        { Icon: BarChart2,label: 'Reports'    },
                    ].map(a => (
                        <button key={a.label} className={styles.quickAction}>
                            <a.Icon size={12} /> {a.label}
                        </button>
                    ))}
                </div>

                {/* CENTER PANEL */}
                <div className={styles.center}>
                    <div className={styles.centerToolbar}>
                        <div className={styles.searchBox}>
                            <Search size={13} color="#9ca3af" />
                            <input
                                className={styles.searchInput}
                                placeholder="Search notifications…"
                                value={searchQuery}
                                onChange={e => setSearchQuery(e.target.value)}
                            />
                        </div>
                        <button className={styles.iconBtn}><Filter size={13} /></button>
                    </div>

                    <div className={styles.statusTabs}>
                        {STATUSES.map(s => (
                            <button
                                key={s.key}
                                className={`${styles.statusTab} ${activeStatus === s.key ? styles.statusTabActive : ''}`}
                                onClick={() => setActiveStatus(s.key)}
                            >
                                <s.Icon size={11} />
                                {s.label}
                                <span className={styles.statusCount}>{statusCounts[s.key]}</span>
                            </button>
                        ))}
                    </div>

                    <div className={styles.notifList}>
                        {filtered.length === 0 ? (
                            <div className={styles.emptyList}>
                                <Sparkles size={28} color="#d1d5db" />
                                <div className={styles.emptyListTitle}>No notifications found</div>
                                <div className={styles.emptyListSub}>Try adjusting your filters or create a new notification.</div>
                            </div>
                        ) : filtered.map(item => (
                            <NotifCard
                                key={item.id}
                                item={item}
                                isSelected={selectedNotif?.id === item.id}
                                onSelect={setSelectedNotif}
                            />
                        ))}
                    </div>
                </div>

                {/* RIGHT DETAIL */}
                <div className={styles.detail}>
                    <DetailPanel item={selectedNotif} />
                </div>
            </div>
        </div>
    )
}

export default NotificationHome
